<template>
  <v-layout row wrap>
    <template v-if="getAuditDocumentData">
      <AuditMetadataDialog
        :dialog-data="getAuditMetadataDialogData"
        :dialog.sync="dialog"
        v-if="dialog"
      />

      <v-flex xs12>
        <v-card>
          <v-toolbar color="secondary lighten-2" dark>
            <v-icon v-if="getAuditDocumentData.protected == true">lock</v-icon>
            <v-toolbar-title>{{ getAuditDocumentData.name }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <CompanyMetadataDialog
              :dialog-data="getAuditCompanyMetadataDialogData"
              v-if="audit.dialog.companyMetadata"
            />
            <v-btn
              icon
              v-if="audit.dialog.audit_attachments"
              :disabled="!getIsOnline"
              @click="attachFile(AuditAttachmentHandler,AttachmentErrorHandler,getAuditId)"
            >
              <v-icon>attach_file</v-icon>
            </v-btn>
            <v-btn icon @click="editItem(getAuditDocumentData)">
              <v-icon>edit</v-icon>
            </v-btn>
            <MoreMenu
              :items="more_menu"
              @itemdelete="deleteItem(getAuditId)"
              @itemedit="editItem(getAuditDocumentData)"
            />
          </v-toolbar>

          <v-layout row wrap>
            <v-flex xs12 sm3 md2 d-flex>
              <v-card flat>
                <v-card-title primary-title>{{ $t('audit_header_scope') }}</v-card-title>
                <v-card-text style="display: block">
                  <span v-if="getAuditDocumentData.standard">
                    {{ $t('audit_header_standard') }}:
                    <snippet-standard :standard="getAuditDocumentData.standard" />
                  </span>
                  <br />
                  <span v-if="getAuditDocumentData.scope">
                    <snippet-scope :scope="getAuditDocumentData.scope" format="header" />
                  </span>
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex xs12 sm3 md2 d-flex>
              <v-card flat>
                <v-card-title primary-title>{{ $t('audit_header_auditors') }}</v-card-title>
                <v-card-text>
                  <span style="border-bottom: 1px solid blue;">
                    <snippet-user :currentUsers="getAuditDocumentData.leadauditor"></snippet-user>
                  </span>
                  <br />
                  <snippet-user :currentUsers="getAuditDocumentData.coauditors"></snippet-user>
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex xs12 sm3 md2 d-flex>
              <v-card flat>
                <v-card-title primary-title>{{ $t('audit_header_planning_date') }}</v-card-title>
                <v-card-text>
                  <SnippetDate :items="getAuditDocumentData.planning_date" />
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex xs12 sm3 md2 d-flex>
              <v-card flat>
                <v-card-title primary-title>{{ $t('audit_header_auditing_date') }}</v-card-title>
                <v-card-text>
                  <SnippetDate :items="getAuditDocumentData.auditing_date" />
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex xs12 sm12 md4 d-flex>
              <v-card flat>
                <v-card-title primary-title>{{ $t('audit_header_status') }}</v-card-title>
                <v-card-text style="padding-top: 10px;">
                  <workflow :status="workflowStatus" @openworkflow="openAuditWorkflow(getAuditId)" />
                </v-card-text>
              </v-card>
            </v-flex>

            <v-flex
              xs12
              sm12
              md4
              d-flex
              v-if="audit.dialog.audit_attachments && getAuditDocumentData.attachments && Object.keys(getAuditDocumentData.attachments).length>0"
            >
              <v-card flat>
                <v-card-title primary-title>{{ $t('audit_header_attachments') }}</v-card-title>
                <v-card-text style="padding-top: 10px;">
                  <ul>
                    <li
                      v-for="(attachment, idx) in getAuditDocumentData.attachments"
                      :title="'Id: ' + attachment.id"
                      :key="idx"
                    >
                      <a :href="attachment.url">{{attachment.name || attachment.bucket_path}}</a>
                    </li>
                  </ul>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>
    </template>
    <template v-else>
      <v-progress-circular indeterminate color="grey" />
    </template>
  </v-layout>
</template>

<i18n>
    {
        "de": {
            "audit_header_scope" : "Auditierter Bereich",
            "audit_header_standard" : "Standard",
            "audit_header_auditors" : "Auditoren",
            "audit_header_planning_date" : "Planungs-Datum",
            "audit_header_auditing_date" : "Geplantes Audit-Datum",
            "audit_header_status" : "Status",
            "audit_header_attachments" : "Anhänge"
        },
        "en": {
            "audit_header_scope" : "Audit Scope",
            "audit_header_standard" : "Standard",
            "audit_header_auditors" : "Auditors",
            "audit_header_planning_date" : "Planning Date",
            "audit_header_auditing_date" : "Audit Date",
            "audit_header_status" : "Status",
            "audit_header_attachments" : "Attachments"
        }
    }
</i18n>

<script>
// Dialogs
import AuditMetadataDialog from "@/components/dialogs/AuditMetadataDialog.vue";
import CompanyMetadataDialog from "@/components/dialogs/CompanyMetadataDialog";

// Snippets / Controls
import MoreMenu from "@/components/snippets/MoreMenu.vue";
import SnippetUser from "@/components/snippets/SnippetUser.vue";
import SnippetScope from "@/components/snippets/SnippetScope.vue";
import SnippetStandard from "@/components/snippets/SnippetStandard.vue";
import SnippetDate from "@/components/snippets/SnippetDate.vue";
import Workflow from "@/components/controls/Workflow.vue";

import { mapActions, mapGetters } from "vuex";
import { api } from "@/store/modules/audits";
import { api as appApi } from "@/store/modules/app";
import { api as confApi } from "@/store/modules/configuration";
import { api as auditApi } from "@/store/modules/audit";
import uuid from "uuid";

import { uploadFile } from "@/utils/uploader";

export default {
  components: {
    AuditMetadataDialog,
    CompanyMetadataDialog,
    MoreMenu,
    SnippetUser,
    SnippetScope,
    SnippetStandard,
    SnippetDate,
    Workflow
  },
  data() {
    return {
      dialog: false,
      more_menu: [
        // TODO - Audits as a Template{ title: "Use as Template", event: "" },
        { title: "Delete Audit", event: "itemdelete" },
        { title: "Edit Audit", event: "itemedit" }
      ]
    };
  },
  computed: {
    ...mapGetters(confApi.namespace, [confApi.getters.audit]),
    ...mapGetters(appApi.namespace, [appApi.getters.getIsOnline]),
    ...mapGetters(auditApi.namespace, [
      auditApi.getters.getAuditDocumentData,
      auditApi.getters.getAuditMetadataDialogData,
      auditApi.getters.getAuditCompanyMetadataDialogData,
      auditApi.getters.getAuditId
    ]),
    workflowStatus() {
      const workflow = this.getAuditDocumentData.workflow;
      if (workflow && workflow.status !== undefined) {
        return workflow.status;
      } else {
        return 0;
      }
    }
  },
  methods: {
    ...mapActions(api.namespace, [
      api.actions.setDialogItem,
      api.actions.setDialogAction,
      api.actions.setAuditId,
      api.actions.deleteAudit,
      api.actions.appendAttachmentToAudit
    ]),
    ...mapActions(appApi.namespace, [
      appApi.actions.toggleDrawerRight,
      appApi.actions.setDrawerRight,
      appApi.actions.addStatus
    ]),

    editItem(item) {
      this.setDialogItem({ ...item });
      this.setDialogAction("update");
      this.dialog = true;
    },
    AuditAttachmentHandler(UploadTaskSnapshot, File, DownloadUrl, AuditId) {
      console.log(
        "AuditAttachmentHandler",
        this,
        UploadTaskSnapshot,
        File,
        DownloadUrl,
        AuditId
      );
      console.log("DownloadUrl", DownloadUrl);
      this.appendAttachmentToAudit({
        AuditId,
        Url: DownloadUrl,
        BucketPath: UploadTaskSnapshot.metadata.fullPath,
        Name: File.name,
        FileUUID: UploadTaskSnapshot.metadata.customMetadata.fileId,
        Activity: {
          id: uuid.v4(),
          avatar: this.$user.avatar(),
          actor: this.$user.id(),
          actor_name: this.$user.displayName(),
          verb:
            this.$user.displayName() +
            " hat dem Audit " +
            AuditId +
            " ein Attachment hinzugefügt",
          object: AuditId,
          object_url: "/audit/" + AuditId,
          timestamp: new Date().toISOString().slice(0, 10)
        }
      });
    },
    AttachmentErrorHandler(Error, File, Payload) {
      console.error("AttachmentErrorHandler", Error, File, Payload);
      this.addStatus({ message: "Upload Failed: " + File.name });
    },
    attachFile(SuccessHandler, ErrorHandler, Payload) {
      console.log("attachFile", SuccessHandler, ErrorHandler, Payload);
      const file_input = document.createElement("input");
      file_input.setAttribute("type", "file");

      file_input.setAttribute(
        "accept",
        this.audit.dialog.audit_attachments_types ||
          "image/*,.doc,.docx,.pdf,.xls,.xlsx"
      );

      file_input.addEventListener("change", changeEvent => {
        console.log("FileChange", changeEvent);
        const files = changeEvent.target.files;

        for (const file of files) {
          console.log("File", file);

          uploadFile(file, {
            auditId: this.getAuditId
          })
            .then(async UploadTaskSnapshot => {
              const DownloadUrl = await UploadTaskSnapshot.ref.getDownloadURL();
              return { UploadTaskSnapshot, DownloadUrl };
            })
            .then(({ DownloadUrl, UploadTaskSnapshot }) => {
              SuccessHandler(UploadTaskSnapshot, file, DownloadUrl, Payload);
            })
            .catch(Error => {
              ErrorHandler(Error, file, Payload);
            });
        }
      });
      file_input.click();
    },
    openAuditWorkflow(audit_id) {
      this.setAuditId(audit_id);
      const payload = {
        component: "VerticalStepperComponent"
      };
      this.setDrawerRight(payload);
      this.toggleDrawerRight();
    },
    async deleteItem(audit_id) {
      const payload = {
        audit_id: audit_id,
        activity: {
          //id: uuid.v4(),
          avatar: this.$user.avatar(),
          actor: this.$user.id(),
          actor_name: this.$user.displayName(),
          verb:
            this.$user.displayName() +
            "hat das Audit " +
            this.editItem.name +
            " gelöscht.",
          object: audit_id,
          object_url: "/audit/" + audit_id,
          timestamp: new Date()
        }
      };

      if (confirm("Are you sure you want to delete this item?")) {
        this.deleteAudit(payload);
        this.$router.push("/");
      }
    }
  }
};
</script>
